import { useEffect, useRef, useState, useMemo } from "react";
import { Globe, Check, Search, X } from "lucide-react";
import { useI18n } from "../i18n";

export const SUPPORTED_LANGS = [
  { code: "ro", countryCode: "ro", label: "Română", subLabel: "Romanian", short: "RO" },
  { code: "en", countryCode: "gb", label: "English", subLabel: "English (US/UK)", short: "EN" },
  { code: "de", countryCode: "de", label: "Deutsch", subLabel: "German", short: "DE" },
  { code: "pl", countryCode: "pl", label: "Polski", subLabel: "Polish", short: "PL" },
  { code: "tr", countryCode: "tr", label: "Türkçe", subLabel: "Turkish", short: "TR" },
  { code: "ru", countryCode: "ru", label: "Русский", subLabel: "Russian", short: "RU" },
  { code: "uk", countryCode: "ua", label: "Українська", subLabel: "Ukrainian", short: "UA" },
  { code: "fr", countryCode: "fr", label: "Français", subLabel: "French", short: "FR" },
  { code: "es", countryCode: "es", label: "Español", subLabel: "Spanish", short: "ES" },
  { code: "it", countryCode: "it", label: "Italiano", subLabel: "Italian", short: "IT" },
  { code: "pt", countryCode: "pt", label: "Português", subLabel: "Portuguese", short: "PT" },
  { code: "cs", countryCode: "cz", label: "Čeština", subLabel: "Czech", short: "CS" },
  { code: "hu", countryCode: "hu", label: "Magyar", subLabel: "Hungarian", short: "HU" },
  { code: "nl", countryCode: "nl", label: "Nederlands", subLabel: "Dutch", short: "NL" },
  { code: "sv", countryCode: "se", label: "Svenska", subLabel: "Swedish", short: "SV" },
  { code: "no", countryCode: "no", label: "Norsk", subLabel: "Norwegian", short: "NO" },
  { code: "da", countryCode: "dk", label: "Dansk", subLabel: "Danish", short: "DA" },
  { code: "fi", countryCode: "fi", label: "Suomi", subLabel: "Finnish", short: "FI" },
  { code: "el", countryCode: "gr", label: "Ελληνικά", subLabel: "Greek", short: "EL" },
  { code: "bg", countryCode: "bg", label: "Български", subLabel: "Bulgarian", short: "BG" },
];

function getStoredLang() {
  const cookieMatch = document.cookie.match(/(?:^|;\s*)googtrans=\/ro\/([a-z-]+)/i);
  if (cookieMatch && cookieMatch[1]) return cookieMatch[1].toLowerCase();
  const local = localStorage.getItem("tf_lang");
  if (local) return local.toLowerCase();
  return "ro";
}

function setTranslationCookie(code) {
  const host = window.location.hostname;
  if (code === "ro") {
    // Clear cookie for default language
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${host};`;
    if (host.includes(".")) {
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${host};`;
    }
  } else {
    const val = `/ro/${code}`;
    document.cookie = `googtrans=${val}; path=/;`;
    document.cookie = `googtrans=${val}; path=/; domain=${host};`;
    if (host.includes(".")) {
      document.cookie = `googtrans=${val}; path=/; domain=.${host};`;
    }
  }
}

export default function GoogleTranslate({ className }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [currentLang, setCurrentLang] = useState(() => getStoredLang());
  const { setLang } = useI18n();
  const ref = useRef(null);

  // Initialize Google Translate Script once
  useEffect(() => {
    window.googleTranslateElementInit = () => {
      try {
        new window.google.translate.TranslateElement({
          pageLanguage: "ro",
          autoDisplay: false,
          includedLanguages: SUPPORTED_LANGS.map(l => l.code).join(",")
        }, "google_translate_hidden_holder");
      } catch (e) {
        console.error("Google Translate Init:", e);
      }
    };

    if (!document.getElementById("google-translate-script")) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else if (window.google && window.google.translate) {
      window.googleTranslateElementInit();
    }
  }, []);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const active = useMemo(() => {
    return SUPPORTED_LANGS.find((l) => l.code === currentLang) || SUPPORTED_LANGS[0];
  }, [currentLang]);

  const filteredLangs = useMemo(() => {
    if (!query.trim()) return SUPPORTED_LANGS;
    const q = query.toLowerCase().trim();
    return SUPPORTED_LANGS.filter(l => 
      l.label.toLowerCase().includes(q) || 
      l.subLabel.toLowerCase().includes(q) || 
      l.short.toLowerCase().includes(q) ||
      l.code.toLowerCase().includes(q)
    );
  }, [query]);

  const handleSelectLang = (code) => {
    setCurrentLang(code);
    setOpen(false);
    setQuery("");

    // Set cookie for full DOM auto translation
    setTranslationCookie(code);
    localStorage.setItem("tf_lang", code);

    // Sync app i18n
    if (setLang) {
      setLang(code === "uk" ? "ua" : code);
    }

    // Trigger Google Translate Engine in the DOM
    const combo = document.querySelector(".goog-te-combo");
    if (combo) {
      combo.value = code;
      combo.dispatchEvent(new Event("change", { bubbles: true, cancelable: true }));
    } else {
      // Reload smoothly so cookie takes immediate full-page effect
      window.location.reload();
    }
  };

  return (
    <div ref={ref} className={`relative ${className || ""}`} data-testid="language-switcher">
      {/* Hidden Google Translate Target Element */}
      <div id="google_translate_hidden_holder" style={{ display: "none" }} />

      {/* Sleek Trigger Button with Flag Image + Code */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="h-8 px-2.5 rounded-[4px] flex items-center gap-2 bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-[var(--tf-primary)]/50 text-zinc-300 hover:text-white transition-all duration-200 hover:scale-105"
        title="Selectează Limba"
        data-testid="language-switcher-btn"
      >
        <img 
          src={`https://flagcdn.com/w40/${active.countryCode}.png`} 
          alt={active.short} 
          className="w-4 h-2.5 object-cover rounded-[2px] shadow-sm shrink-0" 
          loading="lazy"
        />
        <span className="text-xs font-chakra font-black tracking-wider leading-none">{active.short}</span>
      </button>

      {/* Sleek Obsidian Language Popover */}
      {open && (
        <div 
          className="absolute right-0 top-full mt-2 w-64 bg-[#0c0e15] border border-white/15 rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.9)] z-[1000] overflow-hidden animate-scaleUp"
          data-testid="language-dropdown"
        >
          {/* Header */}
          <div className="px-3.5 py-2.5 border-b border-white/10 bg-white/[0.02] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
              <span className="text-[11px] font-chakra font-black uppercase tracking-wider text-zinc-200">
                Alege Limba
              </span>
            </div>
            <span className="text-[10px] font-mono text-zinc-500 font-bold">{SUPPORTED_LANGS.length} limbi</span>
          </div>

          {/* Quick Search */}
          <div className="p-2 border-b border-white/5 bg-black/30">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
              <input
                type="text"
                placeholder="Caută limbă..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                className="w-full bg-[#12151e] border border-white/10 rounded px-2 pl-7 py-1 text-[11px] text-white placeholder-zinc-500 focus:outline-none focus:border-[var(--tf-primary)] transition-all font-sans"
              />
              {query && (
                <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Languages List */}
          <div className="p-1.5 space-y-0.5 max-h-72 overflow-y-auto custom-scrollbar">
            {filteredLangs.length === 0 ? (
              <div className="p-4 text-center text-xs font-mono text-zinc-500">
                Nicio limbă găsită.
              </div>
            ) : (
              filteredLangs.map((item) => {
                const isSelected = item.code === currentLang;
                return (
                  <button
                    key={item.code}
                    onClick={() => handleSelectLang(item.code)}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded transition-all duration-150 text-left ${
                      isSelected
                        ? "bg-[var(--tf-primary)]/15 text-white font-bold border border-[var(--tf-primary)]/40 shadow-sm"
                        : "text-zinc-400 hover:text-white hover:bg-white/[0.06] border border-transparent"
                    }`}
                    data-testid={`lang-option-${item.code}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <img 
                        src={`https://flagcdn.com/w40/${item.countryCode}.png`} 
                        alt={item.short} 
                        className="w-4 h-3 object-cover rounded-[2px] shadow-sm shrink-0" 
                        loading="lazy"
                      />
                      <div className="min-w-0">
                        <div className="text-xs font-chakra font-bold tracking-wide truncate">{item.label}</div>
                        <div className="text-[9px] text-zinc-500 font-mono truncate">{item.subLabel}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${
                        isSelected ? "bg-[var(--tf-primary)] text-black" : "bg-white/5 text-zinc-500"
                      }`}>
                        {item.short}
                      </span>
                      {isSelected && (
                        <Check className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
